import { Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import MuiRadioGroup, { RadioGroupProps } from '@mui/material/RadioGroup';
import { REQUIRED_FIELD_MARKER } from '@shared/appConstants';
import { makeStyles } from '@shared/theme';
import { Fragment, ReactNode, useMemo } from 'react';

const useStyles = makeStyles(({ spacing, typography }) => ({
  formControl: {
    display: 'flex',
  },
  fieldLabel: {
    marginBottom: spacing(2),
  },
  group: {
    margin: 0,
  },
  item: {
    margin: 0,
  },
  rowItem: {
    marginRight: spacing(4),
  },
  itemLabel: {
    ...typography.body1,
  },
  radio: {
    margin: spacing(-1, 0, -1, -3),
    height: spacing(11),
    width: spacing(11),
  },
}));

type Option = {
  value: string;
  label: ReactNode;
};
type Props = Pick<RadioGroupProps, 'onChange' | 'className'> & {
  row?: boolean;
  label?: ReactNode;
  name: string;
  value: string | undefined;
  error?: boolean;
  required?: boolean;
  helperText?: ReactNode;
  onBlur?: () => void;
  options: Option[];
  disabled?: boolean;
  additionalComponents?: { [key: string]: ReactNode };
};

const RadioGroupField: FC<Props> = (props) => {
  const {
    className,
    error,
    helperText,
    required,
    label,
    value,
    options,
    additionalComponents,
    onChange,
    name,
    row = false,
    disabled,
  } = props;
  const { classes, cx } = useStyles();
  const items = useMemo(
    () =>
      options.map((option: Option) => (
        <Fragment key={option.value}>
          <FormControlLabel
            className={cx(classes.item, row ? classes.rowItem : null)}
            classes={{ label: classes.itemLabel }}
            value={option.value}
            control={<Radio color="primary" className={classes.radio} />}
            label={option.label}
            labelPlacement="end"
          />
          {additionalComponents && additionalComponents[option.value]}
        </Fragment>
      )),
    [
      classes.item,
      classes.itemLabel,
      classes.radio,
      classes.rowItem,
      cx,
      options,
      row,
      additionalComponents,
    ],
  );
  return (
    <FormControl
      disabled={disabled}
      className={cx(classes.formControl, className)}
      error={error}
      required={required}
    >
      {label && (
        <Typography variant="subtitle1" className={classes.fieldLabel}>
          {label}
          {required && REQUIRED_FIELD_MARKER}
        </Typography>
      )}
      <MuiRadioGroup
        row={row}
        name={name}
        className={classes.group}
        value={value}
        onChange={onChange}
        onBlur={props.onBlur}
      >
        {items}
      </MuiRadioGroup>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default RadioGroupField;
